import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    X, History, CheckCircle2, GitCompare, RotateCcw,
    Download, RefreshCw, GitBranch, AlertCircle, MoreVertical, Trash2
} from 'lucide-react';
import CustomLoader from '../../../shared/components/ui/CustomLoader';
import { getPptVersions, restorePptVersion, downloadPptVersionReport, deletePptVersion } from '../utils/pptVersionService';
import { extractApiError } from '../utils/apiErrorUtils';
import CompareVersionsModal from './CompareVersionsModal';
import RestoreVersionModal from './RestoreVersionModal';
import DeleteConfirmationModal from '../../../shared/components/modals/DeleteConfirmationModal';
import SuccessToast from '../../../shared/components/ui/SuccessToast';
import './VersionHistoryPanel.css';

/**
 * Formats an ISO timestamp into a human-readable string.
 */
const formatDate = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    const isYesterday = date.toDateString() === yesterday.toDateString();

    const timeStr = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
    if (isToday) return `Today, ${timeStr}`;
    if (isYesterday) return `Yesterday, ${timeStr}`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + `, ${timeStr}`;
};

const VersionHistoryPanel = ({ rfpId, onClose, onRestoreSuccess, currentVersionNumber }) => {
    const [versions, setVersions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [compareVersion, setCompareVersion] = useState(null);
    const [restoreVersion, setRestoreVersion] = useState(null);
    const [deleteVersionObj, setDeleteVersionObj] = useState(null);
    const [openMenuVersionId, setOpenMenuVersionId] = useState(null);
    const [downloadingVersionNum, setDownloadingVersionNum] = useState(null);
    const [toast, setToast] = useState(null);
    const [selectedLang, setSelectedLang] = useState('EN');

    // Role check for deletion permission (Admin / Team Lead / Manager)
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const userDesignation = (user?.designation || '').toLowerCase();
    const userAuthRole = (user?.role || '').toLowerCase();
    const isManager = (
        userDesignation.includes('lead') || userDesignation.includes('manager') || userDesignation.includes('admin') || userDesignation.includes('reviewer') ||
        userAuthRole.includes('lead') || userAuthRole.includes('manager') || userAuthRole.includes('admin') || userAuthRole.includes('reviewer')
    );

    const menuRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) {
                setOpenMenuVersionId(null);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const fetchVersions = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const data = await getPptVersions(rfpId);
            // Sort descending (latest first)
            const sorted = Array.isArray(data)
                ? [...data].sort((a, b) => b.version_number - a.version_number)
                : [];
            setVersions(sorted);
        } catch (err) {
            console.error('Error loading versions:', err);
            setError(extractApiError(err, 'Unable to load version history.'));
        } finally {
            setLoading(false);
        }
    }, [rfpId]);

    useEffect(() => {
        fetchVersions();
    }, [fetchVersions]);

    const latestVersionNumber = versions.length > 0 ? versions[0].version_number : currentVersionNumber;

    const handleRestoreSuccess = (newVersionNumber) => {
        setRestoreVersion(null);
        fetchVersions();
        if (onRestoreSuccess) onRestoreSuccess(newVersionNumber);
    };

    const handleDownloadVersion = async (version) => {
        setDownloadingVersionNum(version.version_number);
        try {
            await downloadPptVersionReport(rfpId, version, selectedLang);
        } catch (err) {
            console.error('Failed to download version:', err);
            alert(extractApiError(err, 'Failed to download PPT version.'));
        } finally {
            setDownloadingVersionNum(null);
        }
    };

    const handleDeleteConfirm = async () => {
        if (!deleteVersionObj) return false;
        try {
            await deletePptVersion(rfpId, deleteVersionObj.version_number);
            setDeleteVersionObj(null);
            await fetchVersions();
            setToast({ message: 'Presentation version deleted successfully', type: 'success' });
            setTimeout(() => setToast(null), 3000);
            return true;
        } catch (err) {
            console.error('Failed to delete version:', err);
            alert(extractApiError(err, 'Failed to delete version.'));
            return false;
        }
    };

    return (
        <>
            <div className="vhp-overlay" onClick={onClose} />
            <aside className="vhp-panel">
                {/* Header */}
                <div className="vhp-header">
                    <div className="vhp-header-title">
                        <History size={18} color="#FF5526" />
                        <h3>Version History</h3>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <select
                            value={selectedLang}
                            onChange={(e) => setSelectedLang(e.target.value)}
                            style={{ padding: '4px 8px', borderRadius: '6px', border: '1px solid #CBD5E1', fontSize: '12px', outline: 'none', background: '#FFFFFF', color: '#334155', fontWeight: '500', cursor: 'pointer' }}
                            title="Downloads will use the selected language"
                        >
                            <option value="EN">EN</option>
                            <option value="AR">AR</option>
                            <option value="ES">ES</option>
                            <option value="PT">PT</option>
                        </select>
                        <button className="vhp-close-btn" onClick={onClose}>
                            <X size={18} />
                        </button>
                    </div>
                </div>

                {/* Version Count */}
                {!loading && !error && (
                    <p className="vhp-count">
                        {versions.length} {versions.length === 1 ? 'version' : 'versions'} saved
                    </p>
                )}

                {/* Content */}
                <div className="vhp-content">
                    {loading ? (
                        <div className="vhp-state-center">
                            <CustomLoader size={24} />
                            <span>Loading version history...</span>
                        </div>
                    ) : error ? (
                        <div className="vhp-state-center vhp-error">
                            <AlertCircle size={28} />
                            <p>{error}</p>
                            <button className="vhp-retry-btn" onClick={fetchVersions}>
                                <RefreshCw size={14} /> Retry
                            </button>
                        </div>
                    ) : versions.length === 0 ? (
                        <div className="vhp-state-center vhp-empty">
                            <GitBranch size={32} opacity={0.4} />
                            <strong>No saved versions yet.</strong>
                            <p>Create a checkpoint to start tracking your presentation changes.</p>
                        </div>
                    ) : (
                        <div className="vhp-timeline">
                            {versions.map((version, index) => {
                                const isCurrent = version.version_number === latestVersionNumber;
                                const isMenuOpen = openMenuVersionId === version.version_number;

                                return (
                                    <div key={version.version_number} className={`vhp-item ${isCurrent ? 'current' : ''}`}>
                                        {/* Timeline dot */}
                                        <div className="vhp-timeline-track">
                                            <div className={`vhp-dot ${isCurrent ? 'current' : ''}`}>
                                                {isCurrent && <CheckCircle2 size={14} color="#FF5526" />}
                                            </div>
                                            {index < versions.length - 1 && <div className="vhp-line" />}
                                        </div>

                                        {/* Version card */}
                                        <div className="vhp-card">
                                            <div className="vhp-card-header">
                                                <div className="vhp-version-label">
                                                    <span>Version {version.version_number}</span>
                                                    {isCurrent && (
                                                        <span className="vhp-current-badge">CURRENT</span>
                                                    )}
                                                </div>

                                                {/* More Options Dropdown Container */}
                                                <div className="vhp-menu-container" ref={isMenuOpen ? menuRef : null}>
                                                    <button
                                                        className="vhp-more-btn"
                                                        title="More options"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setOpenMenuVersionId(prev => prev === version.version_number ? null : version.version_number);
                                                        }}
                                                    >
                                                        <MoreVertical size={16} />
                                                    </button>

                                                    {isMenuOpen && (
                                                        <div className="vhp-dropdown-menu" onClick={(e) => e.stopPropagation()}>
                                                            <button
                                                                className="vhp-menu-item"
                                                                onClick={() => {
                                                                    setOpenMenuVersionId(null);
                                                                    handleDownloadVersion(version);
                                                                }}
                                                                disabled={downloadingVersionNum === version.version_number}
                                                            >
                                                                {downloadingVersionNum === version.version_number ? (
                                                                    <RefreshCw size={14} className="vhp-spin" />
                                                                ) : (
                                                                    <Download size={14} />
                                                                )}
                                                                <span>Download PPT</span>
                                                            </button>

                                                            <button
                                                                className="vhp-menu-item"
                                                                onClick={() => {
                                                                    setOpenMenuVersionId(null);
                                                                    setCompareVersion(version);
                                                                }}
                                                            >
                                                                <GitCompare size={14} />
                                                                <span>Compare</span>
                                                            </button>

                                                            {!isCurrent && (
                                                                <button
                                                                    className="vhp-menu-item"
                                                                    onClick={() => {
                                                                        setOpenMenuVersionId(null);
                                                                        setRestoreVersion(version);
                                                                    }}
                                                                >
                                                                    <RotateCcw size={14} />
                                                                    <span>Revert</span>
                                                                </button>
                                                            )}

                                                            {isManager && (
                                                                <>
                                                                    <div className="vhp-menu-divider" />
                                                                    <button
                                                                        className="vhp-menu-item danger"
                                                                        onClick={() => {
                                                                            setOpenMenuVersionId(null);
                                                                            setDeleteVersionObj(version);
                                                                        }}
                                                                    >
                                                                        <Trash2 size={14} />
                                                                        <span>Delete Version</span>
                                                                    </button>
                                                                </>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="vhp-meta">
                                                {formatDate(version.created_at)}
                                                {(version.created_by_name || version.created_by) && (
                                                    <> &bull; By {version.created_by_name || version.created_by}</>
                                                )}
                                            </div>

                                            {version.comment && (
                                                <div className="vhp-comment">{version.comment}</div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </aside>

            {/* Compare Modal */}
            {compareVersion && (
                <CompareVersionsModal
                    rfpId={rfpId}
                    versions={versions}
                    defaultVersion={compareVersion}
                    currentVersionNumber={latestVersionNumber}
                    onClose={() => setCompareVersion(null)}
                />
            )}

            {/* Restore Modal */}
            {restoreVersion && (
                <RestoreVersionModal
                    rfpId={rfpId}
                    version={restoreVersion}
                    onClose={() => setRestoreVersion(null)}
                    onSuccess={handleRestoreSuccess}
                />
            )}

            {/* Delete Modal */}
            {deleteVersionObj && (
                <DeleteConfirmationModal
                    project={{ title: `Version ${deleteVersionObj.version_number}` }}
                    customTitle="Delete Version?"
                    onClose={() => setDeleteVersionObj(null)}
                    onDeleteConfirm={handleDeleteConfirm}
                />
            )}

            {/* Success Toast */}
            {toast && <SuccessToast toast={toast} onClose={() => setToast(null)} />}
        </>
    );
};

export default VersionHistoryPanel;
